import React, { useState, useRef, useEffect, useCallback, useId } from "react";
import "./Chatbot.css";
import { chatbotKnowledge, findBestMatch } from "./chatbotKnowledge";
import logo from "../../Assets/blackLogo.png";

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const selectRef = useRef(null);
  const instanceId = useId();
  let msgCounter = useRef(0);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const getMessageId = () => {
    msgCounter.current += 1;
    return `${instanceId}-msg-${msgCounter.current}`;
  };

  const toggleChatbot = () => {
    setIsOpen((prev) => {
      const opening = !prev;
      if (opening && messages.length === 0) {
        setMessages([
          {
            id: `${instanceId}-msg-0`,
            text: "Hello! I'm the City Church Assistant. How can I help you today? You can type a question or select one from the dropdown below.",
            sender: "bot",
          },
        ]);
      }
      return opening;
    });
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const processMessage = useCallback(
    (question) => {
      if (question.trim() === "") return;

      const userMessage = { id: getMessageId(), text: question, sender: "user" };
      setMessages((prev) => [...prev, userMessage]);
      setIsTyping(true);

      const botResponse = findBestMatch(question);
      const botMessage = { id: getMessageId(), text: botResponse, sender: "bot" };

      setTimeout(() => {
        setIsTyping(false);
        setMessages((prev) => [...prev, botMessage]);
      }, 600);

      setInputValue("");

      if (selectRef.current) {
        selectRef.current.value = "";
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleSendMessage = () => {
    processMessage(inputValue);
  };

  const handlePredefinedQuestion = (e) => {
    const question = e.target.value;
    if (question) {
      processMessage(question);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const hasInput = inputValue.trim().length > 0;

  return (
    <div className="chatbot-container">
      {!isOpen && (
        <button
          className="chatbot-toggler"
          onClick={toggleChatbot}
          aria-label="Open chat assistant"
        >
          <svg
            width="28"
            height="28"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </button>
      )}

      <div
        className={`chatbot-window ${isOpen ? "chatbot-window--open" : "chatbot-window--closed"}`}
        role="dialog"
        aria-label="Chat assistant"
        aria-hidden={!isOpen}
      >
        {/* Header */}
        <div className="chatbot-header">
          <div className="chatbot-header__info">
            <img
              src={logo}
              alt="The City Church"
              className="chatbot-header__logo"
            />
            <span className="chatbot-header__status">
              <span className="chatbot-header__status-dot" aria-hidden="true" />
              Online
            </span>
          </div>
          <button
            className="chatbot-header__close"
            onClick={toggleChatbot}
            aria-label="Close chat assistant"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Messages */}
        <div
          className="chatbot-messages"
          role="log"
          aria-live="polite"
          aria-label="Chat messages"
        >
          {messages.map((msg) => (
            <div key={msg.id} className={`chatbot-bubble chatbot-bubble--${msg.sender}`}>
              {msg.sender === "bot" && (
                <span className="chatbot-bubble__avatar" aria-hidden="true">
                  CC
                </span>
              )}
              <div className="chatbot-bubble__text">{msg.text}</div>
            </div>
          ))}

          {isTyping && (
            <div className="chatbot-bubble chatbot-bubble--bot chatbot-bubble--typing">
              <span className="chatbot-bubble__avatar" aria-hidden="true">
                CC
              </span>
              <div className="chatbot-typing-indicator" aria-label="Assistant is typing">
                <span />
                <span />
                <span />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Section */}
        <div className="chatbot-input-section">
          <select
            ref={selectRef}
            className="chatbot-select"
            onChange={handlePredefinedQuestion}
            aria-label="Select a predefined question"
            defaultValue=""
          >
            <option value="" disabled>
              Quick questions...
            </option>
            {Object.keys(chatbotKnowledge).map((key) => (
              <option key={key} value={key}>
                {key.charAt(0).toUpperCase() + key.slice(1)}
              </option>
            ))}
          </select>

          <div className={`chatbot-input-row ${hasInput ? "chatbot-input-row--active" : ""}`}>
            <input
              ref={inputRef}
              type="text"
              className="chatbot-input"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
              aria-label="Type a message"
            />
            <button
              className={`chatbot-send ${hasInput ? "chatbot-send--enabled" : ""}`}
              onClick={handleSendMessage}
              disabled={!hasInput}
              aria-label="Send message"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chatbot;
